import React, { useState } from "react";
import { FileSpreadsheet, FileText, FileDown, Printer, X, Loader2 } from "lucide-react";
import { ExportService } from "@/components/insight-rank/keyword-research/services/export/ExportService";
import { generateSEOReport } from "./generateSEOReport";
import { getKeywordsData, getMetricsData } from "./exportDataUtils";

export type ExportTier = "widget" | "chat" | "full";

interface RubyExportDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  tier: ExportTier;
  data: Record<string, any>[];
  fileName: string;
  auditResult?: any;
  domain?: string;
}

const exportService = new ExportService();

const RubyExportDialog: React.FC<RubyExportDialogProps> = ({
  open, onClose, title, tier, data, fileName, auditResult, domain,
}) => {
  const [exporting, setExporting] = useState<string | null>(null);

  if (!open) return null;

  const tierLabel = tier === "widget" ? "Widget Data" : tier === "chat" ? "Audit Report" : "Full Report";

  const handleExport = async (format: "csv" | "excel" | "pdf" | "print") => {
    setExporting(format);
    try {
      const safeName = fileName.replace(/[^a-zA-Z0-9_-]/g, "_");

      switch (format) {
        case "csv":
          exportService.exportToCSV(data);
          break;
        case "excel":
          exportService.exportToExcel(data, safeName);
          break;
        case "pdf":
          if ((tier === "chat" || tier === "full") && auditResult) {
            const auditType = auditResult.requestedAuditType;

            const includeTechnical = ["full-seo-audit", "technical-seo"].includes(auditType);
            const includeReferringDomains = ["authority-trust", "backlinks"].includes(auditType);
            const includeTopPages = ["page-level-audit", "domain-pages"].includes(auditType);
            const { keywordTitle, keywordData } = getKeywordsData(auditResult, auditType);
            const { title: summaryTitle, metrics, summaryMetrics } = getMetricsData(auditResult, auditType);

            generateSEOReport({
              domain: domain || auditResult.domain || "unknown",
              reportTitle: title,
              generatedAt: new Date().toLocaleDateString("en-AU", { year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit" }),
              scoreChange: 0,
              metrics,
              keywordTitle,
              keywords: keywordData,
              summaryMetrics: { title : summaryTitle, metrics: summaryMetrics },
              findings: includeTechnical ? (auditResult.technicalSEO?.issues || []).map(i => ({
                title: i.message, severity: i.severity === "critical" ? "HIGH" : i.severity === "warning" ? "MED" : "LOW", detail: "",
              })) : [],
              technicalIssues: includeTechnical ? (auditResult.technicalSEO?.issues || []).map(i => ({
                issue: i.message, count: 1, severity: i.severity === "critical" ? "HIGH" : i.severity === "warning" ? "MED" : "LOW",
              })) : [],
              positionDistribution: auditType === "keyword-rankings" ? auditResult.positionDistribution : [],
              positionChanges: auditType === "keyword-rankings" ? auditResult.positionChanges : [],
              contentRefreshOpportunities: auditType === "content-gaps" ? auditResult.contentRefreshOpportunities : [],
              trafficHistory: auditResult.history,
              worldwideOverview: auditResult.worldwideOverview,
              referringDomains: includeReferringDomains ? auditResult.backlinks?.topSources : [],
              anchorText: auditResult.backlinks?.anchors,
              serpFeatures: auditResult.serpFeatures,
              competitors: auditType === "competitor-analysis" ? auditResult.competitors : [],
              newLinks: auditType === "backlinks" ? auditResult.backlinks.newLinks : [],
              lostLinks: auditType === "backlinks" ? auditResult.backlinks.lostLinks : [],
              topPages: includeTopPages ? auditResult.topPages : [],
              analysis: auditResult.analysis,
            });
          } else {
            exportService.exportToPDF(data, safeName, title);
          }
          break;
        case "print":
          exportService.print(data, title);
          break;
      }

      onClose();
    } catch (err) {
      console.error(`Export (${format}) failed:`, err);
    } finally {
      setExporting(null);
    }
  };

  const options = [
    { id: "csv" as const, icon: FileSpreadsheet, label: "Export as CSV", desc: "Spreadsheet-compatible format", color: "#4ADE80" },
    { id: "excel" as const, icon: FileSpreadsheet, label: "Export as Excel", desc: "Microsoft Excel with formatting", color: "#3B82F6" },
    { id: "pdf" as const, icon: FileText, label: "Export as PDF", desc: tier === "widget" ? "Simple table PDF" : "Dark-themed branded report", color: "#F87171" },
    { id: "print" as const, icon: Printer, label: "Print", desc: "Send to printer or save via browser", color: "#8B7BF7" },
  ];

  return (
    <div className="fixed inset-0 z-[9999] overflow-y-auto"
      onClick={onClose}>
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" />
      <div className="min-h-full flex items-center justify-center p-4">
        <div className="relative w-full max-w-md rounded-2xl flex flex-col max-h-[90vh] min-h-0"
          onClick={(e) => e.stopPropagation()}
          style={{ background: "rgba(14,10,36,0.95)", border: "1px solid rgba(91,61,245,0.3)", boxShadow: "0 0 40px rgba(91,61,245,0.15)" }}>

          {/* Header */}
          <div className="flex items-center justify-between px-6 pt-5 pb-3">
            <div>
              <h3 className="text-lg font-bold" style={{ color: "#E8E4FF" }}>Export Options</h3>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: "rgba(91,61,245,0.2)", color: "#8B7BF7" }}>{tierLabel}</span>
                <span className="text-xs" style={{ color: "#9AA0B3" }}>{title}</span>
              </div>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-lg transition-all hover:bg-white/10" style={{ color: "#9AA0B3" }}>
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Export options */}
          <div className="px-6 pb-6 space-y-2 overflow-y-auto flex-1 min-h-0">
            {options.map((opt) => {
              const Icon = opt.icon;
              const isExporting = exporting === opt.id;
              return (
                <button key={opt.id} onClick={() => handleExport(opt.id)} disabled={!!exporting}
                  className="w-full flex items-center gap-4 px-4 py-3.5 rounded-xl text-left transition-all hover:scale-[1.01]"
                  style={{
                    background: "rgba(255,255,255,0.03)",
                    border: "1px solid rgba(139,123,247,0.12)",
                    opacity: exporting && !isExporting ? 0.5 : 1,
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(91,61,245,0.1)"; e.currentTarget.style.borderColor = "rgba(91,61,245,0.3)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.03)"; e.currentTarget.style.borderColor = "rgba(139,123,247,0.12)"; }}>
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: `${opt.color}15`, border: `1px solid ${opt.color}30` }}>
                    {isExporting ? <Loader2 className="w-4 h-4 animate-spin" style={{ color: opt.color }} /> : <Icon className="w-4 h-4" style={{ color: opt.color }} />}
                  </div>
                  <div>
                    <div className="text-sm font-medium" style={{ color: "#E8E4FF" }}>{opt.label}</div>
                    <div className="text-xs" style={{ color: "#9AA0B3" }}>{opt.desc}</div>
                  </div>
                  <FileDown className="w-4 h-4 ml-auto flex-shrink-0" style={{ color: "#9AA0B3" }} />
                </button>
              );
            })}
          </div>

          {/* Footer */}
          <div className="px-6 pb-4">
            <div className="text-[10px] text-center" style={{ color: "#6B6585" }}>
              {data.length} {data.length === 1 ? "record" : "records"} • {domain || ""}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RubyExportDialog;
